import React, { useState, useEffect } from 'react';
import { FiEdit2, FiX, FiClock, FiCheckCircle, FiXCircle, FiPlus, FiMinus } from 'react-icons/fi';
import api from '../utils/api';
import toast from 'react-hot-toast';

const BillUpdateRequestModal = ({ order, onClose, onRequestSubmitted }) => {
  const [formData, setFormData] = useState({
    updateReason: '',
    updateType: 'item_modification',
    complimentary: order.complimentary || '',
    items: order.items?.map(item => ({...item})) || [],
    pricing: {
      subtotal: order.pricing?.subtotal || 0,
      gstAmount: order.pricing?.gstAmount || 0,
      couponDiscount: order.pricing?.couponDiscount || 0,
      vendorDiscount: order.pricing?.vendorDiscount || {
        discountType: 'percentage',
        discountValue: 0,
        discountAmount: 0,
        tableDiscountShare: 0
      },
      deliveryCharge: order.pricing?.deliveryCharge || 0,
      discount: order.pricing?.discount || 0,
      total: order.pricing?.total || 0
    },
    discount: {
      amount: order.pricing?.discount || 0,
      type: order.pricing?.vendorDiscount?.discountType || 'percentage'
    },
    gstNumber: order.gstNumber || '',
    isMc: order.isMc || false,
    cashOnly: order.paymentDetails?.splitPayment === false ? (order.paymentDetails?.cashAmount > 0 && order.paymentDetails?.onlineAmount === 0) : false,
    payment: {
      cashAmount: order.paymentDetails?.cashAmount || 0,
      onlineAmount: order.paymentDetails?.onlineAmount || 0
    }
  });
  const [loading, setLoading] = useState(false);
  const [discount, setDiscount] = useState({
    amount: order.pricing?.discount || 0,
    type: order.pricing?.vendorDiscount?.discountType || 'percentage'
  });
  const [gstNumber, setGstNumber] = useState(order.gstNumber || '');
  const [isMc, setIsMc] = useState(order.isMc || false);
  const [cashOnly, setCashOnly] = useState(order.paymentDetails?.splitPayment === false ? (order.paymentDetails?.cashAmount > 0 && order.paymentDetails?.onlineAmount === 0) : false);
  const [payment, setPayment] = useState({
    cashAmount: order.paymentDetails?.cashAmount?.toString() || '0',
    onlineAmount: order.paymentDetails?.onlineAmount?.toString() || '0'
  });

  // Calculate totals when items or discount changes
  useEffect(() => {
    calculateTotals();
  }, [ discount]);

  const calculateTotals = () => {
    let subtotal = formData.items.reduce((sum, item) => sum + (item.subtotal || (item.price * item.quantity)), 0);
    const discountAmount = discount.type === 'percentage' 
      ? (subtotal * discount.amount) / 100 
      : discount.amount;
    
    const afterDiscount = Math.max(0, subtotal - discountAmount);
    
    // GST will be calculated by backend, frontend only handles discount
    const total = afterDiscount + (formData.pricing.deliveryCharge || 0 + formData.pricing?.gstAmount);


    // Update payment amounts if not cash only
    if (!cashOnly && !isMc) {
      const cashAmt = parseFloat(payment.cashAmount) || 0;
      const remainingAmt = Math.max(0, total - cashAmt);
      setPayment(prev => ({
        ...prev,
        onlineAmount: remainingAmt.toFixed(2)
      }));
    }

    setFormData(prev => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        subtotal,
        discount: discountAmount,
        total
      },
      discount,
      gstNumber,
      isMc,
      cashOnly,
      payment
    }));
  };

  const updateItemQuantity = (index, newQuantity) => {
    if (newQuantity < 0) return;
    
    setFormData(prev => {
      const newItems = [...prev.items];
      const item = newItems[index];
      const newQuantityNum = parseInt(newQuantity) || 0;
      item.quantity = newQuantityNum;
      item.subtotal = item.price * newQuantityNum;

      let gstAmount = 0;
      let subtotal = 0;

      for(let item of newItems){
        let price = item.price;
        let quantity = item.quantity;
        let isGSTInclude = item?.menuItemId?.categoryId?.isGSTInclude;
        let gstPercentage = item?.menuItemId?.categoryId?.gstPercentage;

        let itemBaseTotal = 0;
        let itemGSTTotal = 0;

        if (isGSTInclude) {
          const basePrice = price * 100 / (100 + gstPercentage);
          itemBaseTotal = basePrice * quantity;
          itemGSTTotal = (price - basePrice) * quantity;
        } else {
          itemBaseTotal = price * quantity;
          itemGSTTotal = (itemBaseTotal * gstPercentage) / 100;
        }

        subtotal += itemBaseTotal;
        gstAmount += itemGSTTotal;
      }
      console.log(subtotal,"itemitem",gstAmount)
      
      return {
        ...prev,
        items: newItems,
        pricing: {
          ...prev.pricing,
          gstAmount: gstAmount,
          total: gstAmount + subtotal
        }
      };
    });
  };

  const updateItemPrice = (index, newPrice) => {
    if (newPrice < 0) return;
    
    setFormData(prev => {
      const newItems = [...prev.items];
      const item = newItems[index];
      const newPriceNum = parseFloat(newPrice) || 0;
      
      item.price = newPriceNum;
      item.subtotal = newPriceNum * item.quantity;
      
      return {
        ...prev,
        items: newItems
      };
    });
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleDiscountChange = (field, value) => {
    setDiscount(prev => ({
      ...prev,
      [field]: field === 'amount' ? parseFloat(value) || 0 : value
    }));
  };

  const updateTypes = [
    { value: 'item_modification', label: 'Item Modification' },
    { value: 'price_adjustment', label: 'Price Adjustment' },
    { value: 'discount_addition', label: 'Discount Addition' },
    { value: 'special_instruction', label: 'Special Instruction' },
    { value: 'other', label: 'Other' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate that there are items in the bill
    if (formData.items.length === 0) {
      toast.error('Bill must have at least one item');
      return;
    }

    // Validate that all items have quantity > 0
    const hasInvalidItems = formData.items.some(item => item.quantity <= 0 || item.price < 0);
    if (hasInvalidItems) {
      toast.error('All items must have valid quantity and price');
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        items: formData.items,
        pricing: formData.pricing,
        discount: discount,
        updateReason: formData.updateReason,
        updateType: formData.updateType,
        complimentary: formData.complimentary,
        gstNumber: gstNumber,
        isMc: isMc,
        cashOnly: cashOnly,
        payment: payment
      };

      const response = await api.post(`/vendor/orders/${order._id}/bill-update-request`, requestData);
      
      toast.success('Bill update request submitted successfully. Pending approval from vendor owner.');
      onRequestSubmitted(response.data.data);
      onClose();
    } catch (error) {
      console.error('Error submitting bill update request:', error);
      toast.error(error.response?.data?.message || 'Failed to submit bill update request');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <h2 className="text-xl font-bold text-gray-800">
            Request Bill Update - {order.orderNumber}
          </h2>
          <button 
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold leading-none transition-colors" 
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            {/* Order Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <h3 className="font-semibold text-blue-800 mb-2">Order Information</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-blue-600">Order:</span>
                  <span className="text-blue-800 font-semibold">{order.orderNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-600">Bill:</span>
                  <span className="text-blue-800 font-semibold">{order.billNumber || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-600">Total:</span>
                  <span className="text-blue-800 font-semibold">₹{order.pricing?.total || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-600">Customer:</span>
                  <span className="text-blue-800 font-semibold">
                    {order.customerId?.fullName || 
                     order.customerId?.name || 
                     (order.customerId?.mobileNumber ? `Customer (${order.customerId.mobileNumber})` : 
                      (order.customerId?.phone ? `Customer (${order.customerId.phone})` : 'Walk-in'))}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-600">Type:</span>
                  <span className="text-blue-800 font-semibold capitalize">{order.orderType}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-600">Table:</span>
                  <span className="text-blue-800 font-semibold">
                    {order.tableId?.tableNumber || 
                     (order.orderType === 'dine-in' ? 'Not Assigned' : 'N/A')}
                  </span>
                </div>
              </div>
              {order.specialInstructions && (
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <p className="text-blue-600 font-medium text-sm">Special Instructions:</p>
                  <p className="text-blue-800 text-sm">{order.specialInstructions}</p>
                </div>
              )}
            </div>

            {/* Update Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Update Type <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={formData.updateType}
                onChange={(e) => handleInputChange('updateType', e.target.value)}
                required
              >
                {updateTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Update Reason */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Update (Optional)
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                rows="3"
                value={formData.updateReason}
                onChange={(e) => handleInputChange('updateReason', e.target.value)}
                placeholder="Please explain why this bill needs to be updated..."
              />
            </div>

            {/* Bill Items Editor */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">Bill Items</h3>
              <div className="border border-gray-200 rounded-lg p-3 max-h-60 overflow-y-auto">
                {formData.items.map((item, index) => (
                  <div key={index} className="flex items-center gap-2 py-2 border-b border-gray-100">
                    <div className="flex-1">
                      <p className="font-medium text-sm">{item.name}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => updateItemQuantity(index, item.quantity - 1)}
                        className="p-1 text-gray-500 hover:text-red-600"
                      >
                        <FiMinus className="w-3 h-3" />
                      </button>
                      <input
                        type="text"
                        value={item.quantity}
                        onChange={(e) => updateItemQuantity(index, e.target.value)}
                        className="w-12 px-1 py-1 text-center border border-gray-300 rounded text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => updateItemQuantity(index, item.quantity + 1)}
                        className="p-1 text-gray-500 hover:text-green-600"
                      >
                        <FiPlus className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-gray-600">₹</span>
                      <input
                        type="text"
                        disabled
                        value={item.price}
                        onChange={(e) => updateItemPrice(index, e.target.value)}
                        className="w-16 px-1 py-1 text-center border border-gray-300 rounded text-sm"
                      />
                    </div>
                    <div className="text-sm font-medium w-20 text-right">
                      ₹{item.subtotal.toFixed(2)}
                    </div>
                    {/* <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="p-1 text-red-500 hover:text-red-700"
                    >
                      <FiX className="w-4 h-4" />
                    </button> */}
                  </div>
                ))}
                {formData.items.length === 0 && (
                  <p className="text-center text-gray-500 py-4">No items in bill</p>
                )}
              </div>
            </div>

            {/* Discount Section */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">Discount</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Discount Type
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    value={discount.type}
                    onChange={(e) => handleDiscountChange('type', e.target.value)}
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="flat">Flat Amount (₹)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Discount {discount.type === 'percentage' ? 'Percentage' : 'Amount'}
                  </label>
                  <input
                    type="text"
                    value={discount.amount}
                    onChange={(e) => handleDiscountChange('amount', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder={discount.type === 'percentage' ? 'Enter percentage' : 'Enter amount'}
                  />
                </div>
              </div>
            </div>

            {/* Payment Section */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">Payment Details</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    checked={cashOnly}
                    // onChange={(e) => {
                    //   setCashOnly(e.target.checked);
                    //   if (e.target.checked) {
                    //     setPayment({ cashAmount: formData.pricing.total.toFixed(2), onlineAmount: 0 });
                    //     setDiscount({ amount: 0, type: 'percentage' });
                    //   } else {
                    //     setPayment({ cashAmount: '', onlineAmount: formData.pricing.total.toFixed(2) });
                    //   }
                    // }}

                    onChange={(e) => {
                      if (isMc) return;
                      setCashOnly(e.target.checked);

                      if (e.target.checked) {
                        let cashAmount = Number(payment.cashAmount) + Number(payment.onlineAmount);
                        setPayment({ cashAmount: cashAmount, onlineAmount: 0 });
                      } else {
                        let onlineAmount = Number(payment.cashAmount) + Number(payment.onlineAmount);
                        setPayment({ cashAmount: 0, onlineAmount: onlineAmount });
                      }
                    }}
                    
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">Cash Only</span>
                </div>
                
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cash Amount (₹)</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      value={payment.cashAmount}
                      placeholder="Enter cash amount"
                      onChange={(e) => {
                        if (cashOnly || isMc) return;
                        let value = e.target.value.replace(/[^0-9.]/g, '');
                        const parts = value.split('.');
                        if (parts.length > 2) {
                          value = parts[0] + '.' + parts.slice(1).join('');
                        }
                        const cashAmt = parseFloat(value) || 0;
                        const remainingAmt = Math.max(0, formData.pricing.total - cashAmt);
                        setPayment({ cashAmount: value, onlineAmount: remainingAmt.toFixed(2) });
                      }}

                      
                      disabled={cashOnly || isMc}
                    />
                  </div>
                  
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Online Amount (₹)</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      value={payment.onlineAmount}
                      placeholder="Auto-filled"
                      readOnly
                      disabled={cashOnly || isMc}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* GST Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">GST Number</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={gstNumber}
                placeholder="Enter GST Number"
                onChange={(e) => {
                  setGstNumber(e.target.value.toUpperCase());
                  setFormData(prev => ({ ...prev, gstNumber: e.target.value.toUpperCase() }));
                }}
                disabled={isMc}
              />
            </div>

            {/* Complimentary Options */}
            <div>
              <div className="flex items-center mb-2">
                <input
                  type="checkbox"
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  checked={isMc}
                  onChange={(e) => {
                    setIsMc(e.target.checked);
                    if (e.target.checked) {
                      setDiscount({ amount: 0, type: 'percentage' });
                      setPayment({ cashAmount: 0, onlineAmount: 0 });
                      setFormData(prev => ({ ...prev, complimentary: '' }));
                    }
                  }}
                />
                <span className="ml-2 text-sm font-medium text-gray-700">Is Complimentary</span>
              </div>
              
              <label className="block text-sm font-medium text-gray-700 mb-2">Complimentary</label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                value={formData.complimentary}
                placeholder="Enter complimentary (if any)"
                rows="3"
                onChange={(e) => setFormData(prev => ({ ...prev, complimentary: e.target.value }))}
                disabled={isMc}
              />
            </div>

            {/* Pricing Summary */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-2">Pricing Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>₹{formData.pricing.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Discount:</span>
                  <span className="text-red-600">-₹{formData.pricing.discount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>CGST:</span>
                  <span>₹{(formData.pricing.gstAmount / 2).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>SGST:</span>
                  <span>₹{(formData.pricing.gstAmount / 2).toFixed(2)}</span>
                </div>
                {formData.pricing.deliveryCharge > 0 && (
                  <div className="flex justify-between">
                    <span>Delivery Charge:</span>
                    <span>₹{formData.pricing.deliveryCharge.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Total:</span>
                  <span className="text-green-600">₹{formData.pricing.total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            
            {/* Complimentary */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Complimentary
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={formData.complimentary}
                onChange={(e) => handleInputChange('complimentary', e.target.value)}
                placeholder="Any complimentary items or notes..."
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <FiClock className="animate-spin mr-2" />
                  Submitting...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <FiEdit2 className="mr-2" />
                  Submit Request
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BillUpdateRequestModal;
